/* eslint-disable @tencent/i18next/no-literal-string */
import React from 'react';
import { Score } from '@src/slide/application/service/slide-rating-and-improve/view/common/score/score';
import {
    AnalyzerResultDescriptionAndToSelectModel,
    AnalyzerResultType,
    IAnalyzerResult,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import type {
    SlideContentAnalyzer,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer';
import { tencentDocOpenUrlForAll } from '@tencent/tencent_doc_open_url';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import Style from './rating-result.less';
import i18n from '@i18next/i18n';
import Collapse from '@src/slide/application/service/slide-rating-and-improve/view/mobile/result/mobile-collapse';
import TrueImage from './true.svg';
import FalseImage from './false.svg';
import Button from '@tencent/dui-mobile/lib/components/Button';

export enum ResultMode {
    Preview,
    Edit,
}

export interface RatingResultViewProps {
    mode: ResultMode;
    analyzer: SlideContentAnalyzer;
    selectionService?: ISelectionService;
    handleToOptimizeOnPreviewMode?: () => void;
}

export class RatingResult extends React.Component<RatingResultViewProps> {
    public render() {
        const dataSource = this.getDataSource();
        return (
            <div style={{ width: '100%', height: '100%', overflowY: 'scroll' }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}>
                    <div style={{ marginTop: '24px' }}>
                        <Score score={this.props.analyzer.score}/>
                    </div>
                    <span
                        style={{ marginTop: '10px' }}
                        className={Style['rating-result-title']}
                    >
                        {i18n.t('最后得分')}
                    </span>
                    <div
                        style={{ marginTop: '4px', marginBottom: '24px' }}
                        className={Style['rating-result-title-description']}
                    >
                        {this.renderScoreDescription()}
                    </div>
                </div>
                <div style={{
                    position: 'relative',
                    marginRight: '16px',
                    marginLeft: '16px',
                }}>
                    <div style={{
                        position: 'relative',
                        inset: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}>
                        <Collapse items={ this.getResultItems(dataSource) } />
                    </div>
                </div>
                {
                    this.props.mode === ResultMode.Preview && this.props.analyzer.score < 100
                    && <Button
                        type='primary' className={Style['rating-result-to-optimize']}
                        onClick={this.props.handleToOptimizeOnPreviewMode}
                    >{i18n.t('去优化')}</Button>
                }
                <div
                    className={Style['rating-result-suggestion']}
                >
                    {i18n.t('评分不准？')}
                    <span style={{
                        color: '#175ceb',
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        const options = {
                            url: 'https://docs.qq.com/form/page/DZU50akpzWENrbW5w',
                            replace: false,
                            windowName: '_blank',
                        };
                        tencentDocOpenUrlForAll(options);
                    }}
                    >
                        {i18n.t('帮助我们提升')}
                    </span>
                </div>
            </div>
        );
    }

    private getResultItems(dataSource: IAnalyzerResult[]) {
        const items = dataSource.map((item, index) => ({
            title: this.renderItemTitle(item),
            content: this.renderItemContent(item),
            collapsible: item.resultType !== AnalyzerResultType.SUCCESS,
        }));
        return items;
    }

    private renderItemTitle(item: IAnalyzerResult): React.ReactNode {
        const success = item.resultType === AnalyzerResultType.SUCCESS;
        const imgSrc = success ? TrueImage : FalseImage;
        return <>
            <div>
                <img className={Style['collapse-icon']} src={imgSrc}/>
            </div>
            <div className={Style['collapse-text']}>
                <div className={Style['collapse-title-text']}>{item.analyzerName}</div>
                <div className={Style['collapse-description-text']}
                    style={{ marginTop: '4px' }}
                >
                    {item.description}
                </div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex' }}>
                {
                    !success && this.props.mode !== ResultMode.Preview && item.handleProblem
                    && <button
                        style={{ marginRight: '8px' }}
                        className={Style['collapse-handle-button']}
                        onClick={
                            this.onCollapseItemClick.bind(this, item)
                        }
                    >
                        {i18n.t('去优化')}
                    </button>
                }
            </div>
        </>;
    }

    private renderItemContent(item: IAnalyzerResult): React.ReactNode {
        const success = item.resultType === AnalyzerResultType.SUCCESS;
        if (success) return undefined;
        return <div>
            { item.getDescriptionWithSelect()?.map((current, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px' }}
                    onClick={this.handleItemClick.bind(this, current)}
                >
                    <span
                        style={{ width: 'calc(100% - 50px)' }}
                    > { current.description }</span>
                    { this.props.mode === ResultMode.Edit && current.selectElement
                        && <button className={Style['rating-result-item-handle-button']}
                            onClick={this.handleItemButtonClick.bind(this, item, current, index)}> { i18n.t('去处理') }
                        </button>
                    }
                </div>
            ))}
        </div>;
    }

    private handleItemClick(current: AnalyzerResultDescriptionAndToSelectModel) {
        if (current.selectSlide && current.selectElement) {
            this.props.selectionService?.selectedSlides.clear();
            const slideIndex = current.selectSlide
                .presentationRef?.indexOfSlide(current.selectSlide);
            if (slideIndex !== undefined && slideIndex !== -1 && this.props.selectionService) {
                this.props.selectionService.currentSlideIndex = slideIndex;
                this.props.selectionService.selectedElements.clear();
                this.props.selectionService.selectedElements.add(current.selectElement);
            }
        }
    }

    private handleItemButtonClick(
        item: IAnalyzerResult,
        current: AnalyzerResultDescriptionAndToSelectModel,
        index: number,
    ) {
        if (current.selectSlide?.sdkData && current.selectElement?.sdkData) {
            this.props.selectionService?.selectedSlides.clear();
            const slideIndex = current.selectSlide
                .presentationRef?.indexOfSlide(current.selectSlide);
            if (slideIndex !== undefined && slideIndex !== -1 && this.props.selectionService) {
                this.props.selectionService.currentSlideIndex = slideIndex;
                this.props.selectionService.selectedElements.clear();
                this.props.selectionService.selectedElements.add(current.selectElement);
            }
        }
        if (current.handleProblem) {
            current.handleProblem(index);
            this.refresh(item);
            return;
        }
    }

    private onCollapseItemClick(item: IAnalyzerResult, event) {
        event?.stopPropagation?.();
        item.handleProblem?.(() => {
            item.resultType = AnalyzerResultType.SUCCESS;
            this.refresh(item);
        });
    }

    private refresh = (item: IAnalyzerResult) => {
        if (item.resultType === AnalyzerResultType.SUCCESS) {
            this.props.analyzer.calculateScore();
        }
        this.setState({});
    };

    private getDataSource(): Array<IAnalyzerResult> {
        return [
            this.props.analyzer.textAnalyzer.titleFontSizeResult,
            this.props.analyzer.textAnalyzer.subTitleFontSizeResult,
            this.props.analyzer.textAnalyzer.lineSpacingResult,
            this.props.analyzer.textAnalyzer.textOverlapResult,
            this.props.analyzer.colorAnalyzer.textColorContrastResult,
            this.props.analyzer.titleAlignmentResult,
        ];
    }

    private renderScoreDescription() {
        const { score } = this.props.analyzer;
        if (score >= 90) return i18n.t('检测到你的幻灯片非常棒！');
        if (score >= 60) return i18n.t('检测到你的幻灯片有点小问题');
        return i18n.t('检测到你的幻灯片有些问题');
    }
}
